import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {SortSelect} from "shared/ui/sortSelect";
import {IDropdownMenuItem} from "@atomaro/ui-kit/components/DropdownMenu/types";
import Multiselect from "@atomaro/ui-kit/components/Multiselect/Multiselect";
import {DevicesHeadLeft, DevicesHeadRow, DevicesHeadWrapper, InputContainer} from "./styles";
import {Button} from "@atomaro/ui-kit";
import {AddLarge} from "shared/ui/icons/addLarge";
import {useNewDeviceModalStore} from "entities/newDeviceModal";
import {useApiariesStore} from "entities/apiaries";
import {useHivesStore} from "entities/hives";
import {useDevicesStore} from "entities/devices/model/store";
import {HivesRow} from "features/hives";
import Modal from "@atomaro/ui-kit/components/Modal/Modal";
import Input from "@atomaro/ui-kit/components/Input/Input";
import {SearchIcon} from "shared/ui/icons/searchIcon";
import {HiveIcon} from "shared/ui/icons/hiveIcon";
import styled from "styled-components";
import {COLORS} from "shared/consts";
import {toRem} from "shared/helpers";

const ConnectDeviceModalWrapper = styled.div`
    padding: ${toRem(16)};
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: ${toRem(600)};
    min-height: ${toRem(400)};
`;

const ConnectDeviceModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${toRem(16)};

    h3 {
        margin: 0;
        color: ${COLORS.FG.DEFAULT};
        font-size: ${toRem(16)};
        font-weight: 700;
        line-height: ${24 / 16};
    }

    svg {
        cursor: pointer;
    }
`;

const ConnectDeviceModalSubtitle = styled.p`
    margin: 0 0 ${toRem(16)} 0;
    color: ${COLORS.FG.DEFAULT};
    font-size: ${toRem(14)};
    font-weight: 400;
    line-height: ${20 / 14};
`;

const SearchInputWrapper = styled.div`
    margin-bottom: ${toRem(16)};
    position: relative;

    svg {
        position: absolute;
        left: ${toRem(12)};
        top: 50%;
        transform: translateY(-50%);
        width: ${toRem(20)};
        height: ${toRem(20)};
        pointer-events: none;
        z-index: 1;
    }

    input {
        padding-left: ${toRem(40)} !important;
    }
`;

const HivesList = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${toRem(4)};
    overflow-y: auto;
    flex: 1;
    min-height: 0;
    margin-bottom: ${toRem(16)};
`;

const HiveItem = styled.div<{ $selected: boolean }>`
    display: flex;
    align-items: center;
    gap: ${toRem(12)};
    padding: ${toRem(12)} ${toRem(16)};
    background: ${({ $selected }) => ($selected ? COLORS.ACCENT.CONTAINER_SOFT : COLORS.BG.SURFACE1)};
    border-radius: ${toRem(8)};
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
        background: ${({ $selected }) => ($selected ? COLORS.ACCENT.CONTAINER_SOFT : COLORS.BG.SURFACE2)};
    }
`;

const HiveItemIcon = styled.div`
    width: ${toRem(24)};
    height: ${toRem(24)};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg {
        width: 100%;
        height: 100%;
    }
`;

const HiveItemText = styled.div<{ $selected: boolean }>`
    flex: 1;
    color: ${({ $selected }) => ($selected ? COLORS.ACCENT.DEFAULT : COLORS.FG.DEFAULT)};
    font-size: ${toRem(14)};
    font-weight: 500;
    line-height: ${20 / 14};
`;

const EmptyState = styled.div`
    padding: ${toRem(40)} ${toRem(20)};
    text-align: center;
    color: ${COLORS.FG.MUTED};
    font-size: ${toRem(14)};
    line-height: ${20 / 14};
`;

const ModalFooter = styled.div`
    display: flex;
    gap: ${toRem(12)};
    justify-content: flex-end;
    padding-top: ${toRem(16)};
    border-top: 1px solid ${COLORS.BORDER.SOFT};
    margin-top: auto;
`;

export const DevicesHead = () => {
  const sortItems = useRef<IDropdownMenuItem[]>([
    { key: "hiveNumber", value: "Номер улья" },
  ]);

  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
  const openNewDeviceModal = useNewDeviceModalStore((s) => s.open);
  const apiaryId = useApiariesStore((s) => s.currentApiaryId);
  const getHivesFromServer = useHivesStore((s) => s.getHivesFromServer);
  const hives = useHivesStore((s) => s.hives);
  const isLoading = useHivesStore((s) => s.isLoading);
  const setHiveNumbersFilter = useDevicesStore((s) => s.setHiveNumbersFilter);
  const setSort = useDevicesStore((s) => s.setSort);
  const currentSort = useDevicesStore((s) => s.filters);
  const devices = useDevicesStore((s) => s.devices);

  const handleSortChange = useCallback((value: string) => {
    if (!value) return;

    if (value.includes("-ASC")) {
      const sortBy = value.replace("-ASC", "");
      const newSort = currentSort.sort === "ASC" ? "DESC" : "ASC";
      if (sortBy !== currentSort.sortBy || newSort !== currentSort.sort) {
        setSort(sortBy as any, newSort);
      }
    } else {
      const sortBy = value;
      if (sortBy === currentSort.sortBy) {
        const newSort = currentSort.sort === "ASC" ? "DESC" : "ASC";
        setSort(sortBy as any, newSort);
      } else {
        setSort(sortBy as any, "ASC");
      }
    }
  }, [currentSort.sort, currentSort.sortBy, setSort]);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedHive, setSelectedHive] = useState<HivesRow | null>(null);
  const [selectedHiveNumbers, setSelectedHiveNumbers] = useState<string[]>([]);

  // Загружаем ульи при открытии страницы
  useEffect(() => {
    if (apiaryId) {
      getHivesFromServer({ apiaryId, page: { index: 0, size: 200 } });
    }
  }, [apiaryId, getHivesFromServer]);

  // Загружаем ульи для модалки подключения устройства
  useEffect(() => {
    if (isConnectModalOpen && apiaryId) {
      getHivesFromServer({ apiaryId, page: { index: 0, size: 200 } });
    }
  }, [isConnectModalOpen, apiaryId, getHivesFromServer]);

  // Преобразуем ульи в формат для Multiselect
  const hiveItems = useMemo<IDropdownMenuItem[]>(() => {
    return hives.map((hive) => ({
      key: String(hive.number),
      value: `Улей №${hive.number}`,
    }));
  }, [hives]);

  // При изменении выбранных ульев фильтруем устройства
  useEffect(() => {
    setHiveNumbersFilter(selectedHiveNumbers);
  }, [selectedHiveNumbers, setHiveNumbersFilter]);

  useEffect(() => {
    if (!isConnectModalOpen) {
      setSearchQuery("");
      setSelectedHive(null);
    }
  }, [isConnectModalOpen]);

  const filteredHives = useMemo(() => {
    if (!searchQuery.trim()) return hives;
    const query = searchQuery.toLowerCase();
    return hives.filter((hive) => String(hive.number).toLowerCase().includes(query));
  }, [hives, searchQuery]);

  const handleHiveClick = (hive: HivesRow) => {
    setSelectedHive(hive);
  };

  const handleNext = () => {
    if (selectedHive) {
      openNewDeviceModal(selectedHive.id, String(selectedHive.number));
      setIsConnectModalOpen(false);
    }
  };

  return (
    <>
      <DevicesHeadWrapper>
        <DevicesHeadRow>
          <DevicesHeadLeft>
            <InputContainer>
              <Multiselect
                placeholder="Номер улья"
                placement="bottom"
                items={hiveItems}
                size="m"
                dropdownMenuStyle={{ maxHeight: "300px", overflowY: "auto" }}
                onChange={(selected: IDropdownMenuItem[]) => {
                  const numbers = (selected || []).map(item => String(item.key || '')).filter(Boolean);
                  setSelectedHiveNumbers(numbers);
                }}
                autocomplete={{ enabled: true }}
                useInPortal={false}
              />
            </InputContainer>
            <SortSelect
              items={sortItems.current}
              defaultValue="hiveNumber"
              value={currentSort.sortBy}
              direction={currentSort.sort}
              onChange={handleSortChange}
            />
          </DevicesHeadLeft>
          <Button
            size="m"
            colorScheme="accent"
            label={devices.length > 0 ? "Подключить устройство" : "Добавить устройство"}
            iconPrefix={<AddLarge fill="white" />}
            onClick={() => setIsConnectModalOpen(true)}
          />
        </DevicesHeadRow>
      </DevicesHeadWrapper>

      <Modal
        isOpen={isConnectModalOpen}
        centered
        style={{ padding: 0, maxHeight: "90vh", width: "90%", maxWidth: "450px" }}
        onClickOverlay={() => setIsConnectModalOpen(false)}
      >
        <ConnectDeviceModalWrapper>
          <ConnectDeviceModalHeader>
            <h3>Подключение нового устройства</h3>
            <svg onClick={() => setIsConnectModalOpen(false)} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" width="24.000000" height="24.000000" fill="none">
              <rect width="24" height="24" x="0" y="0" />
              <path d="M10.9401 12.0006L4.99902 17.9417L6.05968 19.0023L12.0008 13.0612L17.9418 19.0023L19.0025 17.9417L13.0614 12.0006L19.0025 6.0595L17.9418 4.99884L12.0008 10.9399L6.0597 4.99884L4.99904 6.0595L10.9401 12.0006Z" fill="rgb(16,24,40)" fillRule="evenodd" />
            </svg>
          </ConnectDeviceModalHeader>
          <ConnectDeviceModalSubtitle>
            Выберите улей, к которому хотите подключить устройство
          </ConnectDeviceModalSubtitle>
          <SearchInputWrapper>
            <SearchIcon />
            <Input
              placeholder="Поиск по номеру улья"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="m"
            />
          </SearchInputWrapper>
          {isLoading ? (
            <EmptyState>Загрузка...</EmptyState>
          ) : filteredHives.length === 0 ? (
            <EmptyState>Ульи не найдены</EmptyState>
          ) : (
            <HivesList>
              {filteredHives.map((hive) => (
                <HiveItem
                  key={hive.id}
                  $selected={selectedHive?.id === hive.id}
                  onClick={() => handleHiveClick(hive)}
                >
                  <HiveItemIcon>
                    <HiveIcon />
                  </HiveItemIcon>
                  <HiveItemText $selected={selectedHive?.id === hive.id}>
                    Улей №{hive.number}
                  </HiveItemText>
                </HiveItem>
              ))}
            </HivesList>
          )}
          <ModalFooter>
            <Button
              size="l"
              variant="ghost"
              colorScheme="neutral"
              label="Отменить"
              onClick={() => setIsConnectModalOpen(false)}
            />
            <Button
              size="l"
              colorScheme="accent"
              label="Далее"
              onClick={handleNext}
              disabled={!selectedHive}
            />
          </ModalFooter>
        </ConnectDeviceModalWrapper>
      </Modal>
    </>
  );
};
